import { Box, Button, Grid, Text } from "@chakra-ui/react";
import api from "api/api";
import {
  dropdownConvenioAtendimento,
  dropdownTabulacao
} from "components/atendimentos-components/atendimento-form/fields-data";
import { CalendarFilter } from "components/atendimentos-components/attendance-filter/calendar-filter";
import { AttendanceFilterProps } from "components/atendimentos-components/attendance-filter";
import { parseOrigem } from "components/atendimentos-components/kanban-and-table-atendimento/kanban/parts/utils";
import { RowEmpresaProps } from "components/atendimentos-components/attendance-filter/types";
import { CheckIcon2 } from "components/vectors/checked-box-icon";
import { CustomModal } from "components/custom-modal";
import { NewDropdown } from "components/dropdown/new-dropdown";
import { DropdownOptionProps } from "components/dropdown";
import { Toast } from "components/toast";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import { useApplicationContext } from "contexts/ApplicationContext";
import { isUserGerente } from "utils/is-gerente";
import { createLinkAndDownload } from "utils/create-link-and-download";
import { useState } from "react";
import { PiExportDuotone } from "react-icons/pi";
import { CloseIcon } from "@chakra-ui/icons";
import { apiJWT } from "api/api-jwt";

const eventName = "openExportAttendanceModal";

const origens = Array.from(parseOrigem.entries()).map(([value, name]) => {
  if (name === "LP") name = "Landing Page";
  return { name, value };
});

export function ExportAttendanceModal() {
  const [filter, setFilter] = useState<AttendanceFilterProps>({});
  const [usersList, setUsersList] = useState<DropdownOptionProps[]>([]);
  const [empresasList, setEmpresasList] = useState<DropdownOptionProps[]>([]);
  const [tagsList, setTagsList] = useState<DropdownOptionProps[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { user } = useApplicationContext();
  const isGerente = isUserGerente(user);

  const { isOpen, onClose } = useModalControls(eventName, {
    onOpen: () => {
      setFilter({});
      getUsers();
      getTags();
      if (isGerente) getEmpresas();
    },
    onClose: () => {
      setFilter({});
    }
  });

  const getUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const { data } = await apiJWT.get(
        "/usuarios/possible-transfer-attendance"
      );
      setUsersList(data.map((u: any) => ({ name: u.name, value: u.id })));
    } catch {
      Toast({ title: "Erro ao carregar usuários!", status: "error" });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const getEmpresas = async () => {
    try {
      const { data } = await apiJWT.get<RowEmpresaProps[]>("/customers");
      setEmpresasList([
        { name: "Todos", value: undefined },
        ...data.map(({ id, name, codigoNova }) => {
          if (user.userData.type === "PARCEIRO_GERENTE_VAREJO") {
            return { name, value: id };
          }
          return { name: `${codigoNova} - ${name}`, value: id };
        })
      ]);
    } catch {
      Toast({ title: "Erro ao carregar empresas", status: "error" });
    }
  };

  const getTags = async () => {
    try {
      const { data } = await api.get("/tags/enabled");
      setTagsList(data.map((tag: any) => ({ name: tag.nome, value: tag.id })));
    } catch {
      Toast({ title: "Erro ao carregar tags", status: "error" });
    }
  };

  const onChange = (value: any, key: keyof AttendanceFilterProps) => {
    if (Array.isArray(value))
      setFilter((f) => {
        if (!value.length) {
          const copy = { ...f };
          delete copy[key];
          return copy;
        }
        return { ...f, [key]: value };
      });
    else {
      if (value == null || value === "")
        setFilter((f) => {
          const copy = { ...f };
          delete copy[key];
          return copy;
        });
      else setFilter((f) => ({ ...f, [key]: value }));
    }
  };

  const onChangeMultiselect = (
    value: any,
    field: "usersIds" | "etapa" | "convenios" | "origem" | "tabulacao" | "tags"
  ) => {
    const currentEl = filter[field] as any[] | null;
    if (currentEl?.length && currentEl != null) {
      const isInclude = currentEl.includes(value);
      if (isInclude)
        onChange(
          currentEl.filter((curr: any) => curr !== value),
          field
        );
      else onChange([...filter[field]!, value], field);
    } else onChange([value], field);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const body = { ...filter, limit: 0, offset: 0 };
      const { data } = await api.post("/atendimentos/export", body);
      const file = new File([data], "atendimentos.csv", { type: "text/csv" });
      createLinkAndDownload(file, file.name);
      onClose();
    } catch {
      Toast({ title: "Erro ao exportar os atendimentos", status: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  const modalFooter = (
    <>
      <Button
        variant="outline"
        onClick={onClose}
        leftIcon={<CloseIcon />}
        isDisabled={isExporting}
      >
        Cancelar
      </Button>
      <Button
        leftIcon={<PiExportDuotone size={18} />}
        onClick={handleExport}
        isLoading={isExporting}
        loadingText="Exportando"
      >
        Baixar CSV
      </Button>
    </>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Exportar Atendimentos"
      modalFooter={modalFooter}
      size="md"
      scroll="inside"
    >
      {isGerente ? (
        <Box mb="20px">
          <Text fontSize="14px" fontWeight="medium" mb="8px">
            Empresas
          </Text>
          <NewDropdown
            isLoading={isLoadingUsers}
            options={empresasList}
            onChange={(option) => onChange(option.value, "customerId")}
            value={filter.customerId}
            placeholderRender={(selected) =>
              empresasList.find((crr) => crr.value === selected)?.name
            }
          />
        </Box>
      ) : null}
      <Box mb="20px">
        <Text fontSize="14px" fontWeight="medium" mb="8px">
          Usuário
        </Text>
        <NewDropdown
          multiSelect
          isLoading={isLoadingUsers}
          options={usersList}
          onChange={(option) => onChangeMultiselect(option.value, "usersIds")}
          value={filter.usersIds}
        />
      </Box>
      <Box mb="20px">
        <Text fontSize="14px" fontWeight="medium" mb="8px">
          Origem
        </Text>
        <NewDropdown
          options={[{ name: "Todas", value: undefined }, ...origens]}
          onChange={(option) => onChange(option.value, "origem")}
          value={filter.origem}
          placeholderRender={() =>
            origens.find(({ value }) => value === filter.origem)?.name ??
            "Todas"
          }
        />
      </Box>
      <Box mb="20px">
        <Text fontSize="14px" fontWeight="medium" mb="8px">
          Convênio
        </Text>
        <NewDropdown
          multiSelect
          options={dropdownConvenioAtendimento}
          value={filter.convenios}
          onChange={(option) => onChangeMultiselect(option.value, "convenios")}
        />
      </Box>
      <Box mb="20px">
        <Text fontSize="14px" fontWeight="medium" mb="8px">
          Tags
        </Text>
        <NewDropdown
          multiSelect
          options={tagsList}
          value={filter.tags}
          onChange={(option) => onChangeMultiselect(option.value, "tags")}
        />
      </Box>
      <Box mb="20px">
        <Text fontSize="14px" fontWeight="medium" mb="8px">
          Tabulações
        </Text>
        <NewDropdown
          multiSelect
          options={dropdownTabulacao}
          value={filter.tabulacao}
          onChange={(option) => onChangeMultiselect(option.value, "tabulacao")}
        />
      </Box>
      <Box mb="20px">
        <Text mb="8px" fontWeight="medium">
          Data de criação
        </Text>
        <CalendarFilter
          onChange={onChange}
          filter={filter}
          keyDataInicio="dataCriacaoInicial"
          keyDataFim="dataCriacaoFinal"
        />
      </Box>
      <Box mb="20px">
        <Text mb="8px" fontWeight="medium">
          Ganhos / Perdidos
        </Text>
        <Grid templateColumns="1fr 1fr" borderRadius="10px" overflow="hidden">
          <Button
            borderRadius="5px 0px 0px 5px"
            variant="outline"
            size="sm"
            bg={filter.resultado === "GANHO" ? "green.700" : undefined}
            color={filter.resultado === "GANHO" ? "white" : undefined}
            _hover={
              filter.resultado === "GANHO" ? { bg: "green.600" } : undefined
            }
            _active={
              filter.resultado === "GANHO" ? { bg: "green.700" } : undefined
            }
            transform="translateX(1px)"
            leftIcon={<CheckIcon2 isChecked={filter.resultado === "GANHO"} />}
            onClick={() =>
              onChange(
                filter.resultado === "GANHO" ? undefined : "GANHO",
                "resultado"
              )
            }
          >
            Ganho
          </Button>
          <Button
            borderRadius="0px 5px 5px 0px"
            variant="outline"
            size="sm"
            bg={filter.resultado === "PERDIDO" ? "red.700" : undefined}
            color={filter.resultado === "PERDIDO" ? "white" : undefined}
            _hover={
              filter.resultado === "PERDIDO" ? { bg: "red.600" } : undefined
            }
            _active={
              filter.resultado === "PERDIDO" ? { bg: "red.700" } : undefined
            }
            leftIcon={<CheckIcon2 isChecked={filter.resultado === "PERDIDO"} />}
            onClick={() =>
              onChange(
                filter.resultado === "PERDIDO" ? undefined : "PERDIDO",
                "resultado"
              )
            }
          >
            Perdido
          </Button>
        </Grid>
      </Box>
    </CustomModal>
  );
}

export const openExportAttendanceModal = createModalTrigger(eventName);
